// Per-service SEO copy: H1, intro, plain-language description, local FAQs.
// British English. Southend / SS0–SS9 references throughout.

export type ServiceFaq = { q: string; a: string };
export type ServiceCopy = {
  h1: string;
  intro: string;
  plain: string;
  faqs: ServiceFaq[];
  ctaHeading: string;
  ctaBody: string;
};

const commonFaqs: ServiceFaq[] = [
  {
    q: "Which Southend postcodes do you cover?",
    a: "The full SS0–SS9 patch: Southend-on-Sea, Westcliff, Leigh-on-Sea, Thorpe Bay, Shoeburyness, Rochford, Rayleigh, Hadleigh, Benfleet, Hockley, Canvey Island and Eastwood.",
  },
  {
    q: "Do cleaners carry insurance and DBS?",
    a: "Each profile shows the cleaner's own declared insurance and DBS status. Southend Verified™ cleaners have had their documents reviewed by our admin team. Verified status is available after manual review of required documents, including insurance and DBS evidence where applicable.",
  },
  {
    q: "How do I get a quote?",
    a: "Pick a cleaner and send a free enquiry. They reply directly with availability and a price — we don't take a cut of the job.",
  },
];

export const SERVICE_COPY: Record<string, ServiceCopy> = {
  "home-cleaning": {
    h1: "Home Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "Local home cleaners across Southend-on-Sea, Westcliff, Leigh-on-Sea and the surrounding SS postcodes. Book weekly, fortnightly or a one-off tidy — from a friendly solo cleaner or a small local team.",
    plain:
      "A regular home clean covers kitchens, bathrooms, floors, dusting, hoovering and general tidying. Tell your cleaner about pets, priorities or 'skip this room' and they'll work to your list. Most Southend households book 2 to 3 hours a week.",
    faqs: [
      {
        q: "How much does a home cleaner cost in Southend?",
        a: "Most solo cleaners in SS0–SS9 charge £15–£20 an hour. Two-person teams are usually £25–£35 an hour. One-off deep cleans are quoted per job.",
      },
      {
        q: "Do cleaners bring their own products?",
        a: "Some do, some prefer to use yours — it's set per cleaner and shown on their profile. Eco-friendly and fragrance-free options are common in Southend.",
      },
      {
        q: "Can I book the same cleaner every week?",
        a: "Yes — regular slots are the norm. Once you've matched with a cleaner you like, they'll hold the same day and time for you.",
      },
      {
        q: "Do I need to be home during the clean?",
        a: "Not usually. Most Southend regulars leave a key or a smart lock code once trust is built.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book a local Southend home cleaner",
    ctaBody:
      "Send a free enquiry and get a price back today from listed local cleaners in your SS postcode.",
  },
  "commercial-cleaning": {
    h1: "Commercial Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "Office, retail and small-premises cleaning across Southend-on-Sea, Westcliff, Leigh-on-Sea and the SS0–SS9 area. Early-morning, evening or weekend contracts to fit around your trading hours.",
    plain:
      "Commercial cleans cover floors, kitchens, WCs, desks, glass, bin management and restocking of consumables. Most Southend contracts are 3 to 5 visits a week with a keyholding cleaner and a simple sign-off sheet.",
    faqs: [
      {
        q: "Do you cover Southend high-street and industrial-estate premises?",
        a: "Yes — from small offices off the seafront to units on Progress Road, Comet Way and Airborne Close.",
      },
      {
        q: "Can cleans happen outside business hours?",
        a: "Most commercial cleaners on the site work early mornings, evenings or weekends. You set the window.",
      },
      {
        q: "Do you provide COSHH-compliant products and colour-coded cloths?",
        a: "Many commercial cleaners follow COSHH and BICSc colour-coding by default. Check the profile for method statements.",
      },
      {
        q: "Is there a minimum contract?",
        a: "No fixed minimum — some sites run rolling monthly, others prefer 6 or 12 months for a rate discount.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Get a Southend commercial cleaning quote",
    ctaBody:
      "Tell us your address, hours and site size — we'll match you with local commercial cleaners.",
  },
  "end-of-tenancy": {
    h1: "End of Tenancy Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "Deposit-back end of tenancy cleans across Southend-on-Sea, Westcliff, Leigh-on-Sea and the wider SS postcodes. Tenant-, landlord- and letting-agent-ready to inventory standard.",
    plain:
      "An end of tenancy clean is a full deep clean: oven, extractor, fridge/freezer defrost, limescale removal, inside cupboards, skirting, doors, windows inside, and full bathroom decontamination. It's priced per property, not per hour.",
    faqs: [
      {
        q: "How much is an end of tenancy clean in Southend?",
        a: "Rough guide: 1-bed £120–£160, 2-bed £160–£220, 3-bed £220–£300. Carpets, ovens and outbuildings can be added on.",
      },
      {
        q: "Do you clean carpets as part of end of tenancy?",
        a: "Many Southend cleaners bundle a hot-water extraction carpet clean for £15–£25 per room — ask when you enquire.",
      },
      {
        q: "Is the clean guaranteed against agent check-out?",
        a: "Many end-of-tenancy cleaners offer a 48–72 hour re-clean guarantee if the inventory clerk flags an issue — check each profile.",
      },
      {
        q: "Do you need power and water on?",
        a: "Yes — both must be on and the property empty of belongings so cleaners can reach every surface.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book your Southend end of tenancy clean",
    ctaBody:
      "Send the address and move-out date — get quotes back from local deposit-guarantee cleaners.",
  },
  "window-cleaning": {
    h1: "Window Cleaning in Southend-on-Sea",
    intro:
      "Reliable window cleaners across Southend-on-Sea, Westcliff, Leigh-on-Sea, Shoeburyness and the surrounding SS postcodes. Traditional and pure-water reach-and-wash rounds.",
    plain:
      "Includes glass, frames and sills. Pure-water pole systems reach up to 4 storeys from the ground, safely and without ladders. Most Southend rounds run every 4 or 6 weeks with text reminders.",
    faqs: [
      {
        q: "How much does window cleaning cost in Southend?",
        a: "Typical terraced or semi houses in SS0–SS9 run £10–£18 per visit. Detached and 3-storey properties usually £20–£30.",
      },
      {
        q: "Do you clean windows in coastal salt-spray areas like Thorpe Bay and Shoebury?",
        a: "Yes — a 4-weekly frequency is recommended near the seafront to keep salt haze off the glass.",
      },
      {
        q: "Are conservatory roofs included?",
        a: "Priced separately — most cleaners quote per panel or a flat fee. Ask when you enquire.",
      },
      {
        q: "Do I need to be home?",
        a: "No — outside cleans happen whether you're in or out. You'll get a text or card so you know when payment is due.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Join a Southend window cleaning round",
    ctaBody:
      "Send your postcode — we'll match you with a local round that already runs on your street.",
  },
  "carpet-cleaning": {
    h1: "Carpet Cleaning in Southend-on-Sea",
    intro:
      "Carpet cleaning in Southend-on-Sea, Westcliff-on-Sea, Leigh and every SS0–SS9 postcode. Hot-water extraction, stain treatment and pet-odour removal by DBS-checked, insured and Southend Verified™ local specialists.",
    plain:
      "Hot-water extraction (steam cleaning) lifts dirt, allergens and marks the vacuum can't reach. Most Southend carpets are walk-on-damp in 2–4 hours and fully dry overnight with windows open.",
    faqs: [
      {
        q: "How much does carpet cleaning cost in Southend?",
        a: "Around £25–£40 per room, or £90–£150 for a whole 3-bed house. Stairs, landings and rugs are usually priced separately.",
      },
      {
        q: "How long does carpet take to dry?",
        a: "Walk-on-damp in 2–4 hours with a decent airflow. Avoid heavy furniture for 12 hours.",
      },
      {
        q: "Can pet stains and odours be removed?",
        a: "Most cases yes — enzyme pre-treatment breaks down the odour source, not just the smell. Long-standing urine may need a two-visit approach.",
      },
      {
        q: "Do you cover carpet cleaning in Westcliff-on-Sea?",
        a: "Yes. Westcliff-on-Sea (SS0) is one of the busiest carpet cleaning areas on the site, largely because of the Edwardian terraces and the high student-rental turnover around Hamlet Court Road. Leigh-on-Sea and central Southend-on-Sea are equally well covered.",
      },
      {
        q: "Do you clean upholstery and rugs too?",
        a: "Yes — most carpet cleaners on the site also clean sofas, mattresses and loose rugs. See our sofa and upholstery cleaning page for typical prices and drying times, and ask for the add-on price when you book carpets.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book a Southend carpet clean",
    ctaBody:
      "Tell us the rooms and any problem spots — get quotes back from local extraction specialists.",
  },
  "oven-cleaning": {
    h1: "Oven Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "Deep oven cleaning across Southend-on-Sea, Westcliff, Leigh-on-Sea and the surrounding SS postcodes. Non-caustic dip-tank systems that leave your oven usable the same day.",
    plain:
      "The oven is stripped down: door glass split, racks and trays dipped, interior degreased, seals cleaned. Most single ovens take 60–90 minutes and you can cook that evening.",
    faqs: [
      {
        q: "How much is oven cleaning in Southend?",
        a: "Single oven £55–£70. Double oven £75–£95. Range cookers from £110. Extractors and hobs are usually add-ons.",
      },
      {
        q: "Are the products safe around children and pets?",
        a: "The dip-tank method uses non-caustic, food-safe products. Fumes are minimal and the kitchen is safe to use immediately.",
      },
      {
        q: "Do you clean AGAs, Rangemasters and Neffs?",
        a: "Yes — most Southend oven cleaners handle every major brand including AGA, Rangemaster, Neff slide-and-hide and pyrolytic ovens.",
      },
      {
        q: "Can you replace bulbs and seals?",
        a: "Bulbs are usually a small extra charge; door seals can be replaced if you supply the part or ask them to source it.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book a Southend oven clean",
    ctaBody:
      "Tell us the make and model — get a fixed price back from a local oven-cleaning specialist.",
  },
  "after-builders": {
    h1: "After Builders Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "Post-construction and renovation cleans across Southend-on-Sea, Westcliff, Leigh-on-Sea and the SS0–SS9 area. Dust removal, paint spot removal and sparkle cleans ready for handover.",
    plain:
      "After builders leave, there's fine plaster dust on every surface — including inside cupboards, on light fittings and in tracks. This is a 2-stage clean: full dust-down, then a detailed sparkle clean for handover photos.",
    faqs: [
      {
        q: "How much is after builders cleaning in Southend?",
        a: "Priced per property and how much dust is left. Rough guide: £25–£35/hr for a 2-person team, minimum 4 hours, or a fixed job quote for whole-house builds.",
      },
      {
        q: "Do you remove paint and grout spots from glass?",
        a: "Yes — razor-blading of glass, silicone tidy-up and label residue removal are all standard on a builders clean.",
      },
      {
        q: "Can you clean before the second-fix electricians arrive?",
        a: "Yes — a mid-build dust-down is common. Just book two visits: mid-build and handover.",
      },
      {
        q: "Do you clear away builders' rubbish?",
        a: "Small offcuts and packaging yes; skip-loads of waste no — ask for a waste-removal add-on if needed.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book a Southend after-builders clean",
    ctaBody:
      "Send your address and handover date — get quotes back from local post-construction specialists.",
  },
  "event-cleaning": {
    h1: "Event Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "Pre- and post-event cleaning across Southend-on-Sea, Westcliff, Leigh-on-Sea and the SS0–SS9 area. Weddings, wakes, parties, corporate functions and venue turnarounds.",
    plain:
      "Event cleans cover setup prep, discreet toilet checks during the event, and a full post-event reset: bins, spills, kitchen, WCs, floors and outside frontage. Bookable at short notice for last-minute needs.",
    faqs: [
      {
        q: "Can you cover a wedding or wake at short notice?",
        a: "Yes — most Southend event cleaners take bookings with 24–48 hours notice, and same-day for straightforward turnarounds.",
      },
      {
        q: "Do you clean before AND after the event?",
        a: "Yes — split shifts are common: a light pre-event tidy, on-site cover, then a full reset afterwards.",
      },
      {
        q: "How much does event cleaning cost?",
        a: "Usually £20–£30/hr per cleaner, minimum 3 hours per shift. Larger events are quoted per job.",
      },
      {
        q: "Do you cover venues like Roslin, The Roslin, Park Inn and village halls?",
        a: "Yes — we regularly work in Southend seafront hotels, golf clubs and village halls across SS0–SS9.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book Southend event cleaners",
    ctaBody: "Tell us the venue, date and guest count — get quotes back from local event teams.",
  },
  "deep-cleaning": {
    h1: "Deep & One-off Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "One-off deep cleans across Southend-on-Sea, Westcliff, Leigh-on-Sea and the SS0–SS9 area. Perfect before guests arrive, after illness, or as a spring reset for a home that hasn't had a full clean in a while.",
    plain:
      "A deep clean goes further than a regular visit: skirting, doors, light switches, extractor filters, inside kitchen cupboards, descaling of taps and shower screens, and detailed bathroom work. Typically 6–10 hours for a 3-bed home.",
    faqs: [
      {
        q: "How much is a deep clean in Southend?",
        a: "Rough guide: 1-bed £120–£170, 2-bed £160–£230, 3-bed £220–£320. Priced per job once the cleaner knows the property size and condition.",
      },
      {
        q: "How is a deep clean different from a regular clean?",
        a: "Regular cleans keep a home ticking over. A deep clean resets it — inside appliances, behind furniture, descaling, high-level dust, all in one visit.",
      },
      {
        q: "Can a deep clean be booked at short notice?",
        a: "Often yes — send an enquiry with the address and preferred date and cleaners will confirm availability within a few hours.",
      },
      {
        q: "Do you bring your own products and equipment?",
        a: "Most deep-clean specialists arrive with everything, including a good vacuum, microfibre kit and eco-friendly products. Confirm on the profile.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book a Southend deep clean",
    ctaBody:
      "Tell us the property size and any focus areas — get quotes back from local deep-clean specialists.",
  },
  "airbnb-holiday-let": {
    h1: "Airbnb & Short-let Turnaround Cleaning in Southend (SS0–SS9)",
    intro:
      "Same-day Airbnb, Booking.com and short-let changeovers across Southend seafront, Westcliff, Leigh-on-Sea and the wider SS0–SS9 area. Linen swap, restock, spotless finish and photo-ready reset between guests.",
    plain:
      "A short-let turnaround is a fixed checklist: strip and remake beds with fresh linen, replenish consumables (loo roll, tea, coffee, dishwasher tabs), reset the kitchen, dishwasher run, bins out, quick photo check. Usually 90 minutes to 3 hours.",
    faqs: [
      {
        q: "How much is an Airbnb turnaround in Southend?",
        a: "Typically £45–£65 for a studio/1-bed, £65–£95 for a 2-bed and £95–£140 for a 3-bed — inclusive of linen laundering if the host provides two sets.",
      },
      {
        q: "Can cleaners handle same-day checkout-to-checkin windows?",
        a: "Yes — most short-let cleaners on the site work 11am–3pm turnaround slots as standard, seven days a week.",
      },
      {
        q: "Do you provide linen and towels?",
        a: "Some cleaners offer a linen-rental add-on; most work with two host-supplied sets that get rotated and laundered off-site.",
      },
      {
        q: "What if a guest leaves damage or an unexpected mess?",
        a: "Cleaners photograph anything abnormal and message before starting, so you can raise a resolution centre claim with evidence.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Set up your Southend short-let turnaround",
    ctaBody:
      "Send your listing address and typical guest schedule — we'll match you with a reliable local turnaround cleaner.",
  },
  "upholstery-cleaning": {
    h1: "Upholstery Cleaning in Southend & Essex (SS0–SS9)",
    intro:
      "Sofa, armchair, dining chair and mattress cleaning across Southend-on-Sea, Westcliff, Leigh-on-Sea and the SS0–SS9 area. Hot-water extraction and low-moisture methods matched to the fabric type.",
    plain:
      "Upholstery is pre-treated for stains and odours, then deep-cleaned by hot-water extraction (for cotton/polyester blends) or low-moisture encapsulation (for delicate weaves). Most sofas are dry within 3–6 hours.",
    faqs: [
      {
        q: "How much does upholstery cleaning cost in Southend?",
        a: "Guide prices: 2-seater sofa £45–£65, 3-seater £65–£90, corner unit £90–£140. Dining chairs typically £8–£12 each, mattresses £45–£75.",
      },
      {
        q: "Can you clean fabric and leather?",
        a: "Yes — leather is conditioned and cleaned with pH-neutral products; fabric is extracted. Say what you have when you enquire and the cleaner will confirm the method.",
      },
      {
        q: "Will pet smells and stains come out?",
        a: "Enzyme pre-treatment removes most pet odours at source. Long-standing urine on cushion cores may need a two-visit approach.",
      },
      {
        q: "How long does it take to dry?",
        a: "Fabric upholstery is walk-on-damp in 2–4 hours and fully dry in 6–12 hours with airflow. Leather is dry almost immediately.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book Southend upholstery cleaning",
    ctaBody:
      "Tell us what you'd like cleaned — get quotes from local extraction and low-moisture specialists.",
  },
  "gutter-cleaning": {
    h1: "Gutter Clearance in Southend & Essex (SS0–SS9)",
    intro:
      "Gutter clearance across Southend-on-Sea, Westcliff, Leigh-on-Sea and the SS0–SS9 area. Vacuum-fed reach systems clear leaves, moss and debris from the ground — no ladder work on your roofline.",
    plain:
      "High-power gutter vacuums reach up to four storeys from the ground, with an on-pole camera so the cleaner can show you the state before and after. Downpipes flushed and free-flow tested at the end of the visit.",
    faqs: [
      {
        q: "How much is gutter clearance in Southend?",
        a: "Typical terraced or semi in SS0–SS9: £55–£80. Detached and 3-storey properties usually £80–£140. Bungalows from £45.",
      },
      {
        q: "How often should gutters be cleared?",
        a: "Once a year is standard; twice a year for properties under overhanging trees or near the seafront where salt and moss build faster.",
      },
      {
        q: "Do you clear conservatory roofs and box gutters?",
        a: "Yes — priced separately from the standard eaves. Ask when you enquire so the cleaner brings the right kit.",
      },
      {
        q: "Do I need to be home?",
        a: "No — the work is entirely outside from ground level. You'll get a photo report and free-flow confirmation when it's done.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book a Southend gutter clearance",
    ctaBody:
      "Send your postcode and property type — get quotes back from local vacuum-clearance specialists.",
  },
  "driveway-patio": {
    h1: "Jet Washing & Pressure Washing in Southend (SS0–SS9)",
    intro:
      "Driveway, patio, decking and path pressure washing across Southend-on-Sea, Westcliff, Leigh-on-Sea and the SS0–SS9 area. Rotary surface cleaners for even results, plus optional re-sanding and sealing.",
    plain:
      "Rotary surface cleaners give a streak-free finish on block paving, slabs and concrete. Wet-vac recovery keeps mess off your fencing and beds. Re-sanding (kiln-dried joint sand) is recommended after any deep clean of block paving.",
    faqs: [
      {
        q: "How much is jet washing in Southend?",
        a: "Guide: driveway £180–£320, patio £120–£240, path £60–£120. Sealing is typically £3–£6 per m² on top.",
      },
      {
        q: "Does it damage block paving joints?",
        a: "Any deep clean lifts jointing sand — a proper contractor re-sands with kiln-dried sand at the end so the paving is stable and weed-resistant.",
      },
      {
        q: "Can moss and black spot be removed from patio slabs?",
        a: "Yes — most jobs use a biocide pre-treatment 24–48 hours before the wash so the black-spot lifts cleanly. Ask about a follow-up biocide for lasting results.",
      },
      {
        q: "Do you need my water and power?",
        a: "Most pros run off your outside tap; some bring their own bowser. All confirm before the visit so there are no surprises on the day.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book a Southend jet-wash quote",
    ctaBody:
      "Send the square metres and surface type — get quotes back from local pressure-washing specialists.",
  },
  "ironing-laundry": {
    h1: "Ironing & Laundry Services in Southend (SS0–SS9)",
    intro:
      "Ironing, laundering and bed-linen services across Southend-on-Sea, Westcliff, Leigh-on-Sea and the wider SS0–SS9 area. Weekly pickup and drop-off rounds, plus in-home ironing as an add-on to regular cleans.",
    plain:
      "Ironing is priced per basket or per hour, laundered items include wash, tumble/dry and fold. Bed linen and shirts are the busiest lines. Most rounds run a fixed collection day per postcode.",
    faqs: [
      {
        q: "How much does ironing cost in Southend?",
        a: "Typical £15–£20 per standard laundry basket for ironing only. Wash-and-iron services are usually £3–£5 per kilo, or bundled per bag.",
      },
      {
        q: "Do you collect and deliver?",
        a: "Most Southend ironing services run a weekly round with fixed collection and drop days per postcode. Tell them your address for the local schedule.",
      },
      {
        q: "How long is the turnaround?",
        a: "Usually 24–48 hours. Same-day is possible on shirts if you drop off before 10am.",
      },
      {
        q: "Do you handle delicates and dry-clean-only?",
        a: "Standard laundry services skip dry-clean-only items; some cleaners partner with a local dry cleaner and can add that as a bolt-on.",
      },
      ...commonFaqs,
    ],
    ctaHeading: "Book Southend ironing & laundry",
    ctaBody:
      "Tell us your postcode and volume — get quotes back from local ironing and laundry services.",
  },
};

export function getServiceCopy(slug: string, fallbackName: string): ServiceCopy {
  return (
    SERVICE_COPY[slug] ?? {
      h1: `${fallbackName} in Southend & Essex (SS0–SS9)`,
      intro: `Local ${fallbackName.toLowerCase()} specialists across Southend-on-Sea, Westcliff, Leigh-on-Sea and the surrounding SS postcodes.`,
      plain: `Send a short enquiry and a local ${fallbackName.toLowerCase()} cleaner will reply with availability and a price.`,
      faqs: commonFaqs,
      ctaHeading: `Book a Southend ${fallbackName.toLowerCase()}`,
      ctaBody: `Send a free enquiry and get quotes back from listed local cleaners.`,
    }
  );
}
